import {
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
  useMemo,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { ScrollArea } from "../ui/scroll-area";
import { Separator } from "../ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Calendar,
  FileText,
  Download,
  Clock,
  MapPin,
  ChevronRight,
  Folder,
  FolderOpen,
  Users,
  Image,
  MessageSquare,
  ClipboardList,
  CheckSquare,
  Upload,
  Plus,
  Camera,
  Eye,
  Star,
  ArrowLeft,
  X,
  Send,
  User,
  Video,
  ExternalLink,
  Trash2,
  UserPlus,
  PenTool,
  Check,
  AlertCircle,
  Lock,
  RotateCcw,
  Target,
  Gavel,
  Link,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { getAccountInfo } from "../../../../apis/User";
import {
  fetchBoardMeetings,
  fetchBoardMeeting,
  createBoardMeeting,
  updateBoardMeeting,
  updateMeetingAgendaMinutes,
  updateMeetingParticipant,
  uploadMeetingDocument,
  addMeetingDocumentNote,
  deleteMeetingDocumentNote,
  uploadMeetingFollowUpDoc,
  addFollowUpDocNote,
  deleteFollowUpDocNote,
  createMeetingTodo,
  updateMeetingTodo,
  createMeetingQuestion,
  sendMinutesForApproval as apiSendMinutesForApproval,
  restartMinutesSigning as apiRestartMinutesSigning,
  signMeetingMinutes as apiSignMeetingMinutes,
  createMeetingDecision,
  updateMeetingDecision,
  deleteMeetingDecision,
  uploadMeetingPhoto,
  fetchBoardMembers,
} from "../../services/businessDashApi";
import { normalizeLanguageCode } from "../../../../constants/supportedLanguages";
import { DecisionBlocksSection } from "./DecisionBlock";
import { ExpectedDecisionsSection } from "./ExpectedDecisionsSection";
import {
  MEETING_HUB_FOCUS,
  MEETING_FOCUS_MAX_AGE_MS,
  SESSION_MEETING_FOCUS,
} from "../../lib/boardActionsNavigate";
import {
  PreMeetingPrompt,
  BoardSignalSummary,
  AgendaPlanningPrompt,
  PostMeetingOutcome,
  getUserHasPendingResponses,
} from "../strategy/MeetingIntegration";
import OrganicShape from "./OrganicShape";

// ─── API response normalizer ──────────────────────────────────────────────────

const normalizeNote = (n) => ({
  id: String(n.id),
  author: n.author_name || n.author?.name || n.author || "Unknown",
  content: n.content,
  createdAt: n.created_at || n.createdAt || "",
});

const normalizeDoc = (d) => ({
  id: String(d.id),
  name: d.name,
  type: d.doc_type || d.type || "other",
  size: d.size || "",
  url: d.url || d.file_url || "",
  content: d.content || "",
  notes: (d.notes || []).map(normalizeNote),
});

const normalizeMeeting = (raw) => {
  const rawDate =
    raw.date ||
    raw.start_date ||
    raw.startDate ||
    (raw.scheduled_at && String(raw.scheduled_at).slice(0, 10)) ||
    "";
  const d = new Date(rawDate);
  const quarter =
    raw.quarter != null && raw.quarter !== ""
      ? raw.quarter
      : !Number.isNaN(d.getTime())
        ? Math.ceil((d.getMonth() + 1) / 3)
        : 1;
  return {
    id: String(raw.id),
    date: rawDate,
    name: raw.name,
    type: raw.type || "Board Meeting",
    quarter,
    location: raw.location || undefined,
    onlineLink: raw.online_link || raw.onlineLink || undefined,
    meetingFormat: raw.meeting_format || raw.meetingFormat || "in-person",
    attendees: raw.attendees || [],
    documents: (raw.documents || []).map(normalizeDoc),
    status: raw.status || "upcoming",
    minutes: raw.minutes || "",
    agendaItems: (raw.agenda_items || raw.agendaItems || []).map((a) => ({
      id: String(a.id),
      title: a.title,
      description: a.description || "",
      minutes: a.minutes || "",
    })),
    photos: (raw.photos || []).map((p) => ({
      id: String(p.id),
      name: p.name || "",
      category: p.category || "other",
      url: p.image || p.url || "/placeholder.svg",
    })),
    participants: (raw.participants || []).map((p) => ({
      id: String(p.id),
      name: p.name,
      email: p.email || "",
      role: p.role || "",
      present: p.present || false,
    })),
    todos: (raw.todos || []).map((t) => ({
      id: String(t.id),
      task: t.task,
      assignee: t.assignee || "",
      completed: t.completed || false,
      dueDate: t.due_date || t.dueDate || "",
    })),
    followUpDocs: (raw.followup_docs || raw.followUpDocs || []).map(
      normalizeDoc,
    ),
    questions: (raw.questions || []).map((q) => ({
      id: String(q.id),
      question: q.question,
      author: q.author_name || q.author?.name || q.author || "Unknown",
      answered: q.answered || false,
      answer: q.answer || undefined,
    })),
    expectedDecisions: (
      raw.expected_decisions ||
      raw.expectedDecisions ||
      []
    ).map((e) => ({
      id: String(e.id),
      title: e.title,
      decisionType: e.decision_type || e.decisionType || "strategy",
      expectedOutcome: e.expected_outcome || e.expectedOutcome || "decision",
      description:
        e.description || e.decision_text || e.decisionText || undefined,
    })),
    decisions: (raw.decisions || []).map((dec) => ({
      id: String(dec.id),
      title: dec.title,
      decisionType: dec.decision_type || dec.decisionType || "strategy",
      expectedOutcome:
        dec.expected_outcome || dec.expectedOutcome || "decision",
      decisionText: dec.decision_text || dec.decisionText || "",
      votes: (dec.votes || []).map((v) => {
        const pName = v.participant_name || v.participantName || v.name || "";
        return {
          participantId: v.participant_id || v.participantId || pName,
          participantName: pName,
          vote: v.vote,
          votedAt: v.voted_at || v.votedAt || "",
        };
      }),
      decisionOwner: dec.decision_owner || dec.decisionOwner || "",
      followUpRequired: dec.follow_up_required || dec.followUpRequired || false,
      followUpDescription:
        dec.follow_up_description || dec.followUpDescription || "",
      reviewDate: dec.review_date || dec.reviewDate || "",
      stakeholdersAffected: [],
      status: dec.status || "pending",
    })),
    externalGuests: (raw.external_guests || raw.externalGuests || [])
      .filter(Boolean)
      .map((g) => ({
        id: String(g.id),
        name: g.name || "",
        email: g.email || "",
      })),
    materialLinks: (raw.material_links || raw.materialLinks || [])
      .filter(Boolean)
      .filter((l) => l.title && l.url)
      .map((l) => ({
        id: String(l.id),
        title: l.title,
        url: l.url,
      })),
  };
};

// ─── Mock data ────────────────────────────────────────────────────────────────

const MOCK_MEETINGS = [
  {
    id: "1",
    date: "2025-01-20",
    name: "Q1 Preparation Meeting",
    type: "Board Meeting",
    quarter: 1,
    location: "Conference Room A",
    onlineLink: "https://teams.microsoft.com/meet/q1-prep-2025",
    meetingFormat: "hybrid",
    attendees: [
      "Sarah Johnson",
      "Michael Brown",
      "Emily Davis",
      "Robert Wilson",
      "Lisa Anderson",
    ],
    documents: [
      {
        id: "d1",
        name: "Q1 Planning Document",
        type: "report",
        size: "2.4 MB",
        content:
          "Q1 PLANNING DOCUMENT\n\nExecutive Summary\n\nThis document outlines the key strategic initiatives for Q1 2025.",
        notes: [
          {
            id: "dn1",
            author: "Sarah Johnson",
            content: "Please review the budget allocations before the meeting.",
            createdAt: "2025-01-18 14:30",
          },
        ],
      },
      {
        id: "d2",
        name: "Meeting Agenda",
        type: "agenda",
        size: "156 KB",
        content:
          "MEETING AGENDA\n\nQ1 Preparation Meeting\nDate: January 20, 2025\n\n1. Welcome and Roll Call\n2. Q1 Budget Review\n3. Strategic Initiatives\n4. Action Items",
        notes: [],
      },
    ],
    status: "completed",
    minutes:
      "Meeting started at 10:00. Discussed Q1 targets and budget allocations. Key decisions: Approved marketing budget increase of 15%.",
    agendaItems: [
      {
        id: "a1",
        title: "Welcome and Roll Call",
        description: "Opening of the meeting",
        minutes: "Meeting opened at 10:00. All members present.",
      },
      {
        id: "a2",
        title: "Q1 Budget Review",
        description: "Review of Q1 budget allocations",
        minutes: "Marketing budget increase of 15% approved unanimously.",
      },
      {
        id: "a3",
        title: "Strategic Initiatives",
        description: "Key strategic initiatives",
        minutes: "Three main initiatives identified.",
      },
      {
        id: "a4",
        title: "Action Items",
        description: "Assignment of action items",
        minutes: "Action items assigned to department heads.",
      },
    ],
    photos: [
      {
        id: "p1",
        name: "Conference Room Setup",
        category: "building",
        url: "/placeholder.svg",
      },
      {
        id: "p2",
        name: "Team Photo",
        category: "participants",
        url: "/placeholder.svg",
      },
    ],
    participants: [
      {
        id: "part1",
        name: "Sarah Johnson",
        role: "Chairperson",
        present: true,
      },
      { id: "part2", name: "Michael Brown", role: "Vice Chair", present: true },
      {
        id: "part3",
        name: "Emily Davis",
        role: "Board Member",
        present: false,
      },
      {
        id: "part4",
        name: "Robert Wilson",
        role: "Board Member",
        present: true,
      },
      { id: "part5", name: "Lisa Anderson", role: "Secretary", present: true },
    ],
    todos: [
      {
        id: "t1",
        task: "Prepare Q1 financial summary",
        assignee: "Michael Brown",
        completed: true,
        dueDate: "2025-02-01",
      },
      {
        id: "t2",
        task: "Send meeting notes to stakeholders",
        assignee: "Lisa Anderson",
        completed: false,
        dueDate: "2025-01-25",
      },
    ],
    followUpDocs: [
      {
        id: "fd1",
        name: "Action Items Summary.pdf",
        type: "PDF",
        size: "245 KB",
        content:
          "ACTION ITEMS SUMMARY\n\nQ1 Preparation Meeting - January 20, 2025\n\n1. Prepare Q1 financial summary - Michael Brown\n2. Send meeting notes - Lisa Anderson",
        notes: [
          {
            id: "n1",
            author: "Sarah Johnson",
            content:
              "Please ensure all action items are tracked in project management.",
            createdAt: "2025-01-21 09:30",
          },
        ],
      },
    ],
    questions: [
      {
        id: "q1",
        question: "What is the projected ROI for Q1?",
        author: "Robert Wilson",
        answered: true,
        answer: "Estimated 12% based on current projections.",
      },
    ],
    expectedDecisions: [],
    decisions: [],
  },
  {
    id: "2",
    date: "2025-03-15",
    name: "Q1 Board Meeting",
    type: "Board Meeting",
    quarter: 1,
    location: "Conference Room A",
    meetingFormat: "in-person",
    attendees: [
      "Sarah Johnson",
      "Michael Brown",
      "Emily Davis",
      "Robert Wilson",
      "Lisa Anderson",
    ],
    documents: [
      {
        id: "d3",
        name: "Q1 Financial Report",
        type: "report",
        size: "2.4 MB",
        content:
          "Q1 FINANCIAL REPORT\n\nRevenue: $1,250,000\nExpenses: $980,000\nNet Profit: $270,000",
        notes: [],
      },
      {
        id: "d4",
        name: "Meeting Agenda",
        type: "agenda",
        size: "156 KB",
        content:
          "Q1 BOARD MEETING AGENDA\n\n1. Opening Remarks\n2. Q1 Financial Report\n3. Strategic Review",
        notes: [],
      },
      {
        id: "d5",
        name: "Strategic Review",
        type: "presentation",
        size: "4.8 MB",
        content:
          "STRATEGIC REVIEW PRESENTATION\n\nQ1 2025 Strategic Initiatives",
        notes: [],
      },
    ],
    status: "upcoming",
    agendaItems: [
      {
        id: "a6",
        title: "Opening Remarks",
        description: "Welcome and introductions",
      },
      {
        id: "a7",
        title: "Q1 Financial Report",
        description: "Review of Q1 financial performance",
      },
      {
        id: "a8",
        title: "Strategic Review",
        description: "Presentation of strategic initiatives",
      },
    ],
    photos: [],
    participants: [],
    todos: [],
    followUpDocs: [],
    questions: [],
    expectedDecisions: [],
    decisions: [],
  },
  {
    id: "3",
    date: "2025-04-20",
    name: "Annual General Meeting",
    type: "Company Event",
    quarter: 2,
    location: "Main Auditorium",
    onlineLink: "https://zoom.us/j/agm-2025",
    meetingFormat: "hybrid",
    attendees: ["All Board Members", "Executive Team", "Key Stakeholders"],
    documents: [
      {
        id: "d7",
        name: "AGM Presentation",
        type: "presentation",
        size: "8.2 MB",
        content: "ANNUAL GENERAL MEETING PRESENTATION\n\n2024 Year in Review",
        notes: [],
      },
      {
        id: "d8",
        name: "Annual Report 2024",
        type: "report",
        size: "12.5 MB",
        content:
          "ANNUAL REPORT 2024\n\nChairman's Letter\n\nDear Stakeholders,\n\nI am pleased to present our Annual Report for 2024.",
        notes: [],
      },
    ],
    status: "upcoming",
    agendaItems: [
      {
        id: "a11",
        title: "Welcome Address",
        description: "Opening by the Chairman",
      },
      {
        id: "a12",
        title: "Annual Report Presentation",
        description: "Review of 2024 performance",
      },
      {
        id: "a13",
        title: "Election of Board Members",
        description: "Voting on board composition",
      },
    ],
    photos: [],
    participants: [],
    todos: [],
    followUpDocs: [],
    questions: [],
    expectedDecisions: [],
    decisions: [],
  },
  {
    id: "4",
    date: "2025-06-15",
    name: "Q2 Board Meeting",
    type: "Board Meeting",
    quarter: 2,
    onlineLink: "https://teams.microsoft.com/meet/q2-board",
    meetingFormat: "online",
    attendees: [
      "Sarah Johnson",
      "Michael Brown",
      "Emily Davis",
      "Robert Wilson",
      "Lisa Anderson",
    ],
    documents: [
      {
        id: "d9",
        name: "Q2 Review",
        type: "report",
        size: "2.1 MB",
        content:
          "Q2 REVIEW\n\nRevenue: $1,350,000\nExpenses: $1,020,000\nNet Profit: $330,000",
        notes: [],
      },
      {
        id: "d10",
        name: "Meeting Agenda",
        type: "agenda",
        size: "142 KB",
        content:
          "Q2 BOARD MEETING AGENDA\n\n1. Q2 Performance Review\n2. Market Analysis\n3. Strategic Adjustments",
        notes: [],
      },
    ],
    status: "upcoming",
    agendaItems: [
      {
        id: "a16",
        title: "Q2 Performance Review",
        description: "Review of Q2 results",
      },
      {
        id: "a17",
        title: "Market Analysis",
        description: "Competitive landscape update",
      },
    ],
    photos: [],
    participants: [],
    todos: [],
    followUpDocs: [],
    questions: [],
    expectedDecisions: [],
    decisions: [],
  },
  {
    id: "5",
    date: "2024-12-15",
    name: "Q4 Board Meeting 2024",
    type: "Board Meeting",
    quarter: 4,
    location: "Conference Room A",
    meetingFormat: "in-person",
    attendees: ["All Board Members"],
    documents: [
      {
        id: "d11",
        name: "Annual Review",
        type: "report",
        size: "4.2 MB",
        content:
          "ANNUAL REVIEW 2024\n\nTotal Revenue: $5,200,000\nTotal Expenses: $4,100,000\nNet Profit: $1,100,000",
        notes: [],
      },
      {
        id: "d12",
        name: "Meeting Agenda",
        type: "agenda",
        size: "156 KB",
        content:
          "Q4 BOARD MEETING AGENDA\n\n1. Year-End Review\n2. 2025 Budget Approval\n3. Board Evaluation",
        notes: [],
      },
    ],
    status: "completed",
    minutes:
      "Year-end review completed. All Q4 objectives met. Budget for 2025 approved unanimously.",
    agendaItems: [
      {
        id: "a19",
        title: "Year-End Review",
        description: "Review of 2024 performance",
        minutes: "All Q4 objectives were met. Revenue exceeded targets by 8%.",
      },
      {
        id: "a20",
        title: "2025 Budget Approval",
        description: "Vote on 2025 budget",
        minutes: "Budget for 2025 approved unanimously. Total: $4.5M",
      },
      {
        id: "a21",
        title: "Board Evaluation",
        description: "Annual board self-assessment",
        minutes: "Positive feedback overall. Areas for improvement identified.",
      },
    ],
    photos: [
      {
        id: "p3",
        name: "Year End Celebration",
        category: "lunch",
        url: "/placeholder.svg",
      },
    ],
    participants: [
      {
        id: "part11",
        name: "Sarah Johnson",
        role: "Chairperson",
        present: true,
      },
      {
        id: "part12",
        name: "Michael Brown",
        role: "Vice Chair",
        present: true,
      },
    ],
    todos: [
      {
        id: "t3",
        task: "Finalize 2025 budget document",
        assignee: "Finance Team",
        completed: true,
        dueDate: "2024-12-20",
      },
    ],
    followUpDocs: [
      {
        id: "fd2",
        name: "2025 Budget Approved.pdf",
        type: "PDF",
        size: "1.8 MB",
        content:
          "2025 BUDGET DOCUMENT\n\nApproved by the Board - December 15, 2024\n\nTotal Annual Budget: $4,500,000",
        notes: [],
      },
    ],
    questions: [],
    expectedDecisions: [],
    decisions: [],
  },
];

// CURRENT_USER is resolved at runtime from the auth API — see component state below

// Invitee categories are loaded from the board members API — see component state below

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getMeetingsForYear = (meetings, year) =>
  meetings
    .filter((m) => {
      const t = new Date(m.date).getTime();
      return !Number.isNaN(t) && new Date(m.date).getFullYear() === year;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

/** Prefer current year, then fall back to the most recent year that actually has meetings (fixes empty list when mocks/API only have past years). */
const pickDefaultMeetingYear = (meetingsList) => {
  const y = new Date().getFullYear();
  const candidates = [y, y - 1, y - 2, y - 3, y - 4];
  for (const yr of candidates) {
    if (getMeetingsForYear(meetingsList, yr).length > 0) return yr;
  }
  return y;
};

/** Invalid BCP-47 tags (or odd i18n.language values) make toLocaleDateString throw — blanking the whole list. */
function safeToLocaleDateString(value, locale, options) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const loc =
    typeof locale === "string" && locale.trim() ? locale.trim() : "en";
  const attempts = [loc, loc.split("-")[0], "en"].filter(Boolean);
  const uniq = [...new Set(attempts)];
  for (const tryLoc of uniq) {
    try {
      return d.toLocaleDateString(tryLoc, options);
    } catch {
      /* next */
    }
  }
  try {
    return d.toLocaleDateString(undefined, options);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Flatten DRF-style `{ error: "validation_error", details: { ... } }` for toasts. */
function formatMeetingValidationDetails(details) {
  if (!details || typeof details !== "object") return "";
  const parts = [];
  const walk = (val, prefix) => {
    if (Array.isArray(val)) {
      const pfx = prefix || "request";
      val.forEach((item, idx) => {
        if (typeof item === "string") parts.push(`${pfx}[${idx}]: ${item}`);
        else if (item && typeof item === "object" && item.errors) {
          const base = `${pfx}[${item.index ?? idx}]`;
          Object.entries(item.errors).forEach(([field, msgs]) => {
            const m = Array.isArray(msgs) ? msgs.join(" ") : String(msgs);
            parts.push(`${base}.${field}: ${m}`);
          });
        } else if (item && typeof item === "object")
          walk(item, `${pfx}[${idx}]`);
        else parts.push(`${pfx}[${idx}]: ${String(item)}`);
      });
    } else if (val && typeof val === "object") {
      Object.entries(val).forEach(([k, v]) =>
        walk(v, prefix ? `${prefix}.${k}` : k),
      );
    } else {
      parts.push(`${prefix}: ${String(val)}`);
    }
  };
  walk(details, "");
  return parts.length ? parts.join("\n") : JSON.stringify(details);
}

/** Lovable-style hero above Create Meeting (copy uses t() defaultValue so no extra locale files required). */
function MeetingTabHero({ t }) {
  return (
    <div className="flex flex-col lg:flex-row items-start gap-8 mb-8 lg:mb-12 mt-4">
      <div className="flex flex-col items-center gap-4 w-full lg:w-1/6">
        <OrganicShape className="p-6 flex flex-col items-center gap-3 min-h-[12rem] w-full">
          <h1 className="text-xl font-semibold text-foreground tracking-wide text-center">
            {t("tabHeroTitle", { defaultValue: "MEETING" })}
          </h1>
          <div className="w-12 h-20 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <svg
              className="w-6 h-10 text-primary-foreground"
              viewBox="0 0 16 28"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden
            >
              <rect x="1" y="1" width="14" height="26" rx="2" ry="2" />
              <circle cx="8" cy="8" r="3" />
              <path d="M4 16 L12 16" />
              <path d="M4 20 L12 20" />
              <path d="M4 24 L10 24" />
            </svg>
          </div>
        </OrganicShape>
      </div>
      <OrganicShape
        variant="secondary"
        className="flex-1 w-full p-6 bg-gradient-hero min-h-[14rem]"
      >
        <div className="space-y-4 h-full flex flex-col justify-center">
          <h2 className="text-xl font-semibold text-foreground mb-2 lg:mb-4">
            {t("tabHeroHeadline", {
              defaultValue: "What happens before, during and after meetings?",
            })}
          </h2>
          <div className="space-y-3">
            <p className="text-sm text-foreground/90 leading-relaxed">
              {t("tabHeroDescription", {
                defaultValue:
                  "Access meeting materials, track attendance, record decisions, and follow up on action items. Everything you need to prepare for and document board meetings.",
              })}
            </p>
            <p className="text-sm text-foreground/80 leading-relaxed">
              {t("tabHeroSubdescription", {
                defaultValue:
                  "Meeting archives are organized by year for easy reference and compliance.",
              })}
            </p>
          </div>
        </div>
      </OrganicShape>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * @param {{ isTabActive?: boolean }} props — when false, skips consuming deep links (Meeting tab not visible).
 */
const MeetingSection = ({ isTabActive = true } = {}) => {
  const { t, i18n } = useTranslation("meeting");
  const locale =
    normalizeLanguageCode(i18n.resolvedLanguage || i18n.language) || "en";

  const formatMeetingDate = useCallback(
    (ds) =>
      safeToLocaleDateString(ds, locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
    [locale],
  );

  const formatMeetingFormatLabel = useCallback(
    (fmt) =>
      t(`format_${String(fmt || "").replace(/-/g, "_")}`, {
        defaultValue: fmt || "",
      }),
    [t],
  );

  const formatStatusLabel = useCallback(
    (st) =>
      t(`status_${String(st || "").replace(/-/g, "_")}`, {
        defaultValue: st || "",
      }),
    [t],
  );

  const [currentUser, setCurrentUser] = useState({ name: "", email: "" });
  const [inviteeCategories, setInviteeCategories] = useState({});
  const [selectedYear, setSelectedYear] = useState(() =>
    pickDefaultMeetingYear(MOCK_MEETINGS),
  );
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [activeFolder, setActiveFolder] = useState("preparation");
  const [meetings, setMeetings] = useState(MOCK_MEETINGS);
  const prepDocInputRef = useRef(null);
  const createPrepDocsInputRef = useRef(null);
  const followupDocInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [viewedFolders, setViewedFolders] = useState({});
  const [newQuestion, setNewQuestion] = useState("");
  const [newTodo, setNewTodo] = useState({
    task: "",
    assignee: "",
    dueDate: "",
  });
  const [viewingDoc, setViewingDoc] = useState(null);
  const [viewingPrepDoc, setViewingPrepDoc] = useState(null);
  const [newNoteContent, setNewNoteContent] = useState("");
  const [newPrepNote, setNewPrepNote] = useState("");
  const [editingMinutes, setEditingMinutes] = useState({});
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [lockedMinutes, setLockedMinutes] = useState({ 1: true, 5: true });
  const [minutesSigs, setMinutesSigs] = useState({
    1: [
      { name: "Sarah Johnson", status: "signed", signedAt: "2025-01-22 10:30" },
      { name: "Michael Brown", status: "signed", signedAt: "2025-01-22 14:15" },
      { name: "Robert Wilson", status: "pending" },
      { name: "Lisa Anderson", status: "pending" },
    ],
    5: [
      { name: "Sarah Johnson", status: "signed", signedAt: "2024-12-18 09:00" },
      { name: "Michael Brown", status: "signed", signedAt: "2024-12-18 11:30" },
    ],
  });
  const [docSigStatus, setDocSigStatus] = useState({});

  const [newMeeting, setNewMeeting] = useState({
    name: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    location: "",
    onlineLink: "",
    meetingFormat: "in-person",
    selectedInvitees: [],
    agendaItems: [],
    newAgendaTitle: "",
    newAgendaDescription: "",
    externalLinks: [],
    newLinkTitle: "",
    newLinkUrl: "",
    manualName: "",
    manualEmail: "",
    expectedDecisions: [],
    draftPrepDocuments: [],
  });

  const meetingLayoutRef = useRef(null);

  const tryConsumeMeetingDeepLink = useCallback(async () => {
    let raw;
    try {
      raw = sessionStorage.getItem(SESSION_MEETING_FOCUS);
    } catch {
      return;
    }
    if (!raw) return;
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem(SESSION_MEETING_FOCUS);
      return;
    }
    if (!parsed?.id) {
      sessionStorage.removeItem(SESSION_MEETING_FOCUS);
      return;
    }
    if (Date.now() - (parsed.ts || 0) > MEETING_FOCUS_MAX_AGE_MS) {
      sessionStorage.removeItem(SESSION_MEETING_FOCUS);
      return;
    }
    sessionStorage.removeItem(SESSION_MEETING_FOCUS);
    try {
      const detail = await fetchBoardMeeting(String(parsed.id));
      if (!detail) return;
      const normalized = normalizeMeeting(detail);
      setSelectedMeeting(normalized);
      const y = normalized.date ? new Date(normalized.date).getFullYear() : NaN;
      if (!Number.isNaN(y)) setSelectedYear(y);
      const folder = parsed.activeFolder;
      if (typeof folder === "string" && folder) setActiveFolder(folder);
      setViewingDoc(null);
      setViewingPrepDoc(null);
    } catch {
      toast.error(
        t("meetingOpenFailed", { defaultValue: "Could not open the linked meeting." }),
      );
    }
  }, [t]);

  useEffect(() => {
    if (!isTabActive) return;
    tryConsumeMeetingDeepLink();
  }, [isTabActive, tryConsumeMeetingDeepLink]);

  useEffect(() => {
    const onFocus = () => {
      if (isTabActive) tryConsumeMeetingDeepLink();
    };
    window.addEventListener(MEETING_HUB_FOCUS, onFocus);
    return () => window.removeEventListener(MEETING_HUB_FOCUS, onFocus);
  }, [isTabActive, tryConsumeMeetingDeepLink]);

  // When this section becomes visible or language reflows layout, nudge sizing (also see flex split below vs resizable-panels).
  useLayoutEffect(() => {
    const el = meetingLayoutRef.current;
    const nudge = () => window.dispatchEvent(new Event("resize"));
    requestAnimationFrame(() => {
      requestAnimationFrame(nudge);
    });
    const t0 = setTimeout(nudge, 0);
    const t1 = setTimeout(nudge, 120);
    if (!el || typeof ResizeObserver === "undefined") {
      return () => {
        clearTimeout(t0);
        clearTimeout(t1);
      };
    }
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(nudge);
    });
    ro.observe(el);
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, [i18n.language, i18n.resolvedLanguage]);

  // ── Load current user + invitee list from APIs on mount ──────────────────
  useEffect(() => {
    getAccountInfo()
      .then((res) => {
        const profile = res?.data?.data?.profile || res?.data?.profile || {};
        const name =
          [profile.firstName, profile.familyName].filter(Boolean).join(" ") ||
          profile.email ||
          "";
        const email = profile.email || "";
        if (name || email) setCurrentUser({ name, email });
      })
      .catch(() => {});

    fetchBoardMembers()
      .then((data) => {
        const list = Array.isArray(data)
          ? data
          : (data?.data ?? data?.results ?? []);
        const active = list.filter(
          (m) => m.is_active !== false && m.approval_status !== "rejected",
        );
        // Group by backend role value
        const roleLabel = (role) => {
          switch (role) {
            case "president":
              return t("role_president");
            case "vice-president":
              return t("role_vicePresident");
            case "employee":
              return t("role_employee");
            default:
              return t("role_ordinary");
          }
        };
        const categoryLabel = (role) => {
          if (role === "president" || role === "vice-president")
            return t("cat_leadership");
          if (role === "employee") return t("cat_employeeMembers");
          return t("cat_ordinaryMembers");
        };
        const grouped = {};
        active.forEach((m) => {
          const cat = categoryLabel(m.role);
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push({
            name: m.name || "",
            email: m.email || "",
            role: roleLabel(m.role),
          });
        });
        if (Object.keys(grouped).length > 0) setInviteeCategories(grouped);
      })
      .catch(() => {});
  }, [t]);

  // ── Load meetings from backend when year changes ──────────────────────────
  useEffect(() => {
    const load = async () => {
      setIsLoadingMeetings(true);
      try {
        const data = await fetchBoardMeetings(selectedYear);
        const list = Array.isArray(data)
          ? data
          : (data?.results ?? data?.data ?? []);
        if (list.length > 0) {
          setMeetings((prev) => {
            const otherYears = prev.filter(
              (m) => new Date(m.date).getFullYear() !== selectedYear,
            );
            return [...otherYears, ...list.map(normalizeMeeting)];
          });
        }
      } catch {
        // Keep mock data as fallback
      } finally {
        setIsLoadingMeetings(false);
      }
    };
    load();
  }, [selectedYear]);

  // ── Load full meeting detail when a meeting is selected ───────────────────
  useEffect(() => {
    if (!selectedMeeting) return;
    const loadDetail = async () => {
      try {
        const data = await fetchBoardMeeting(selectedMeeting.id);
        if (data) {
          const normalized = normalizeMeeting(data);
          setSelectedMeeting(normalized);
          setMeetings((prev) =>
            prev.map((m) => (m.id === normalized.id ? normalized : m)),
          );
          // Sync locked/signature state from backend
          if (data.minutes_locked !== undefined) {
            setLockedMinutes((prev) => ({
              ...prev,
              [normalized.id]: !!data.minutes_locked,
            }));
          }
          if (data.signatures?.length > 0) {
            setMinutesSigs((prev) => ({
              ...prev,
              [normalized.id]: data.signatures.map((s) => ({
                name: s.participant_name,
                status: s.status,
                signedAt: s.signed_at || undefined,
              })),
            }));
          }
        }
      } catch {
        // Keep current local state
      }
    };
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeeting?.id]);

  const years = [
    new Date().getFullYear(),
    new Date().getFullYear() - 1,
    new Date().getFullYear() - 2,
  ];

  const FOLDERS = useMemo(
    () => [
      {
        id: "preparation",
        name: t("folder_preparation"),
        icon: <FileText className="w-4 h-4" />,
      },
      {
        id: "board-signal",
        name: t("folder_board_signal"),
        icon: <Target className="w-4 h-4" />,
      },
      {
        id: "questions",
        name: t("folder_questions"),
        icon: <MessageSquare className="w-4 h-4" />,
      },
      {
        id: "minutes",
        name: t("folder_minutes"),
        icon: <ClipboardList className="w-4 h-4" />,
      },
      {
        id: "pictures",
        name: t("folder_pictures"),
        icon: <Image className="w-4 h-4" />,
      },
      {
        id: "participants",
        name: t("folder_participants"),
        icon: <Users className="w-4 h-4" />,
      },
      {
        id: "document-yield",
        name: t("folder_follow_up"),
        icon: <CheckSquare className="w-4 h-4" />,
      },
    ],
    [t],
  );

  const folderHasContent = (m, fid) => {
    if (!m) return false;
    switch (fid) {
      case "preparation":
        return m.documents?.length > 0 || m.materialLinks?.length > 0;
      case "board-signal":
        return true;
      case "questions":
        return m.questions?.length > 0;
      case "minutes":
        return !!m.minutes || m.agendaItems?.length > 0;
      case "pictures":
        return m.photos?.length > 0;
      case "participants":
        return m.participants?.length > 0 || m.externalGuests?.length > 0;
      case "document-yield":
        return m.todos?.length > 0 || m.followUpDocs?.length > 0;
      default:
        return false;
    }
  };

  const isFolderUnviewed = (mid, fid) =>
    !(viewedFolders[mid] || []).includes(fid);

  const markViewed = (mid, fid) =>
    setViewedFolders((prev) => {
      const cur = prev[mid] || [];
      if (cur.includes(fid)) return prev;
      return { ...prev, [mid]: [...cur, fid] };
    });

  const handleFolderClick = (fid) => {
    setActiveFolder(fid);
    if (selectedMeeting) markViewed(selectedMeeting.id, fid);
  };

  const shouldShowBoardSignalFlag = () => getUserHasPendingResponses();

  const updateMeeting = (updated) => {
    setSelectedMeeting(updated);
    setMeetings((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
  };

  // ── Participants ────────────────────────────────────────────────────────────

  const togglePresence = async (pid) => {
    if (!selectedMeeting) return;
    const participant = selectedMeeting.participants.find((p) => p.id === pid);
    const newPresent = !participant?.present;
    updateMeeting({
      ...selectedMeeting,
      participants: selectedMeeting.participants.map((p) =>
        p.id === pid ? { ...p, present: newPresent } : p,
      ),
    });
    try {
      await updateMeetingParticipant(selectedMeeting.id, pid, {
        present: newPresent,
      });
    } catch {
      /* local state already updated */
    }
  };

  // ── Todos ───────────────────────────────────────────────────────────────────

  const toggleTodo = async (tid) => {
    if (!selectedMeeting) return;
    const todo = selectedMeeting.todos.find((t) => t.id === tid);
    const newCompleted = !todo?.completed;
    updateMeeting({
      ...selectedMeeting,
      todos: selectedMeeting.todos.map((t) =>
        t.id === tid ? { ...t, completed: newCompleted } : t,
      ),
    });
    try {
      await updateMeetingTodo(selectedMeeting.id, tid, {
        completed: newCompleted,
      });
    } catch {
      /* local state already updated */
    }
  };

  const addTodo = async () => {
    if (!selectedMeeting || !newTodo.task.trim()) return;
    const tempId = Date.now().toString();
    updateMeeting({
      ...selectedMeeting,
      todos: [
        ...selectedMeeting.todos,
        { id: tempId, ...newTodo, completed: false },
      ],
    });
    setNewTodo({ task: "", assignee: "", dueDate: "" });
    toast.success(t("todoAdded"));
    try {
      const created = await createMeetingTodo(selectedMeeting.id, newTodo);
      if (created?.id) {
        setMeetings((prev) =>
          prev.map((m) =>
            m.id === selectedMeeting.id
              ? {
                  ...m,
                  todos: m.todos.map((t) =>
                    t.id === tempId ? { ...t, id: String(created.id) } : t,
                  ),
                }
              : m,
          ),
        );
      }
    } catch {
      /* local state already updated */
    }
  };

  // ── Questions ───────────────────────────────────────────────────────────────

  const addQuestion = async () => {
    if (!selectedMeeting || !newQuestion.trim()) return;
    const text = newQuestion;
    const tempId = Date.now().toString();
    updateMeeting({
      ...selectedMeeting,
      questions: [
        ...selectedMeeting.questions,
        {
          id: tempId,
          question: text,
          author: currentUser.name,
          answered: false,
        },
      ],
    });
    setNewQuestion("");
    toast.success(t("questionAdded"));
    try {
      const created = await createMeetingQuestion(selectedMeeting.id, text);
      if (created?.id) {
        setMeetings((prev) =>
          prev.map((m) =>
            m.id === selectedMeeting.id
              ? {
                  ...m,
                  questions: m.questions.map((q) =>
                    q.id === tempId ? { ...q, id: String(created.id) } : q,
                  ),
                }
              : m,
          ),
        );
      }
    } catch {
      /* local state already updated */
    }
  };

  // ── Notes ───────────────────────────────────────────────────────────────────

  const addNote = async (docId, content, isPrep) => {
    if (!selectedMeeting || !content.trim()) return;
    const note = {
      id: Date.now().toString(),
      author: currentUser.name,
      content,
      createdAt: new Date().toLocaleString("en-GB").replace(",", ""),
    };
    if (isPrep) {
      const updDoc = {
        ...viewingPrepDoc,
        notes: [...viewingPrepDoc.notes, note],
      };
      const updM = {
        ...selectedMeeting,
        documents: selectedMeeting.documents.map((d) =>
          d.id === docId ? updDoc : d,
        ),
      };
      updateMeeting(updM);
      setViewingPrepDoc(updDoc);
      setNewPrepNote("");
    } else {
      const updDoc = { ...viewingDoc, notes: [...viewingDoc.notes, note] };
      const updM = {
        ...selectedMeeting,
        followUpDocs: selectedMeeting.followUpDocs.map((d) =>
          d.id === docId ? updDoc : d,
        ),
      };
      updateMeeting(updM);
      setViewingDoc(updDoc);
      setNewNoteContent("");
    }
    toast.success(t("noteAdded"));
    try {
      if (isPrep) {
        await addMeetingDocumentNote(selectedMeeting.id, docId, content);
      } else {
        await addFollowUpDocNote(selectedMeeting.id, docId, content);
      }
    } catch {
      /* local state already updated */
    }
  };

  const deleteNote = async (noteId, docId, isPrep) => {
    if (!selectedMeeting) return;
    if (isPrep) {
      const updDoc = {
        ...viewingPrepDoc,
        notes: viewingPrepDoc.notes.filter((n) => n.id !== noteId),
      };
      const updM = {
        ...selectedMeeting,
        documents: selectedMeeting.documents.map((d) =>
          d.id === docId ? updDoc : d,
        ),
      };
      updateMeeting(updM);
      setViewingPrepDoc(updDoc);
    } else {
      const updDoc = {
        ...viewingDoc,
        notes: viewingDoc.notes.filter((n) => n.id !== noteId),
      };
      const updM = {
        ...selectedMeeting,
        followUpDocs: selectedMeeting.followUpDocs.map((d) =>
          d.id === docId ? updDoc : d,
        ),
      };
      updateMeeting(updM);
      setViewingDoc(updDoc);
    }
    toast.success(t("noteDeleted"));
    try {
      if (isPrep) {
        await deleteMeetingDocumentNote(selectedMeeting.id, docId, noteId);
      } else {
        await deleteFollowUpDocNote(selectedMeeting.id, docId, noteId);
      }
    } catch {
      /* local state already updated */
    }
  };

  // ── Minutes ─────────────────────────────────────────────────────────────────

  const updateAgendaMinutes = async (agendaId, text) => {
    if (!selectedMeeting) return;
    updateMeeting({
      ...selectedMeeting,
      agendaItems: selectedMeeting.agendaItems.map((a) =>
        a.id === agendaId ? { ...a, minutes: text } : a,
      ),
    });
    try {
      await updateMeetingAgendaMinutes(selectedMeeting.id, agendaId, text);
    } catch {
      /* local state already updated */
    }
  };

  const sendToApproval = async (mid) => {
    const m = meetings.find((x) => x.id === mid);
    if (!m) return;
    setLockedMinutes((prev) => ({ ...prev, [mid]: true }));
    const signatories = m.participants
      .filter((p) => p.present)
      .map((p) => p.name);
    const names = signatories.length > 0 ? signatories : m.attendees;
    setMinutesSigs((prev) => ({
      ...prev,
      [mid]: names.map((name) => ({ name, status: "pending" })),
    }));
    toast.success(t("minutesSentTo", { count: names.length }));
    try {
      await apiSendMinutesForApproval(mid);
      // API returns {status, signatory_count, penneo_case_id} — not a signatures array.
      // Refetch the full meeting detail to get the actual signature records.
      const detail = await fetchBoardMeeting(mid);
      if (detail?.signatures?.length > 0) {
        setMinutesSigs((prev) => ({
          ...prev,
          [mid]: detail.signatures.map((s) => ({
            name: s.participant_name,
            status: s.status,
            signedAt: s.signed_at || undefined,
          })),
        }));
      }
    } catch {
      /* local state already updated */
    }
  };

  const restartSigning = async (mid) => {
    setLockedMinutes((prev) => ({ ...prev, [mid]: false }));
    setMinutesSigs((prev) => {
      const n = { ...prev };
      delete n[mid];
      return n;
    });
    toast.info(t("signingRestarted"));
    try {
      await apiRestartMinutesSigning(mid);
    } catch {
      /* local state already updated */
    }
  };

  const signDoc = (docId, docName) => {
    setDocSigStatus((prev) => ({ ...prev, [docId]: "signed" }));
    toast.success(t("signedDocPenneo", { name: docName }));
  };

  const signMinutes = async (mid) => {
    setMinutesSigs((prev) => {
      const cur = prev[mid] || [];
      return {
        ...prev,
        [mid]: cur.map((s) =>
          s.name === currentUser.name && s.status === "pending"
            ? {
                ...s,
                status: "signed",
                signedAt: new Date().toLocaleString("da-DK"),
              }
            : s,
        ),
      };
    });
    toast.success(t("signedMinutesPenneo"));
    try {
      await apiSignMeetingMinutes(mid);
    } catch {
      /* local state already updated */
    }
  };

  // ── File uploads ────────────────────────────────────────────────────────────

  const handleUploadPrepDoc = async (file) => {
    if (!file || !selectedMeeting) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    toast.info(t("uploadingDoc"));
    try {
      const res = await uploadMeetingDocument(selectedMeeting.id, formData);
      const doc = normalizeDoc(res?.data ?? res);
      updateMeeting({
        ...selectedMeeting,
        documents: [...selectedMeeting.documents, doc],
      });
      toast.success(t("uploadedDoc", { name: doc.name }));
    } catch {
      toast.error(t("uploadFailed"));
    }
  };

  const handleUploadFollowupDoc = async (file) => {
    if (!file || !selectedMeeting) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);
    toast.info(t("uploadingDoc"));
    try {
      const res = await uploadMeetingFollowUpDoc(selectedMeeting.id, formData);
      const doc = normalizeDoc(res?.data ?? res);
      updateMeeting({
        ...selectedMeeting,
        followUpDocs: [...selectedMeeting.followUpDocs, doc],
      });
      toast.success(t("uploadedDoc", { name: doc.name }));
    } catch {
      toast.error(t("uploadFailed"));
    }
  };

  const handleUploadPhoto = async (file) => {
    if (!file || !selectedMeeting) return;
    const formData = new FormData();
    formData.append("image", file);
    formData.append("name", file.name);
    toast.info(t("uploadingPhoto"));
    try {
      const raw = await uploadMeetingPhoto(selectedMeeting.id, formData);
      const photo = {
        id: String(raw?.id ?? Date.now()),
        name: raw?.name || file.name,
        category: raw?.category || "other",
        url: raw?.url || URL.createObjectURL(file),
      };
      updateMeeting({
        ...selectedMeeting,
        photos: [...selectedMeeting.photos, photo],
      });
      toast.success(t("photoUploaded"));
    } catch {
      toast.error(t("photoUploadFailed"));
    }
  };

  // ── Decisions ───────────────────────────────────────────────────────────────

  const updateDecisions = async (updatedDecisions) => {
    if (!selectedMeeting) return;
    updateMeeting({ ...selectedMeeting, decisions: updatedDecisions });
  };

  const handleAddDecision = async (decision) => {
    if (!selectedMeeting) return;
    const updated = [...(selectedMeeting.decisions || []), decision];
    updateMeeting({ ...selectedMeeting, decisions: updated });
    try {
      const res = await createMeetingDecision(selectedMeeting.id, {
        title: decision.title,
        decision_type: decision.decisionType,
        expected_outcome: decision.expectedOutcome,
        status: decision.status || "pending",
      });
      if (res?.id) {
        updateMeeting({
          ...selectedMeeting,
          decisions: updated.map((d) =>
            d.id === decision.id ? { ...d, id: String(res.id) } : d,
          ),
        });
      }
    } catch {
      /* local state already updated */
    }
  };

  const handleUpdateDecision = async (decisionId, data) => {
    if (!selectedMeeting) return;
    updateMeeting({
      ...selectedMeeting,
      decisions: (selectedMeeting.decisions || []).map((d) =>
        d.id === decisionId ? data : d,
      ),
    });
    try {
      await updateMeetingDecision(selectedMeeting.id, decisionId, {
        decision_text: data.decisionText,
        decision_owner: data.decisionOwner,
        status: data.status,
        follow_up_required: data.followUpRequired,
        follow_up_description: data.followUpDescription,
        review_date: data.reviewDate || null,
        votes: (data.votes || []).map((v) => ({
          participant_name: v.participantName || v.participant_name,
          vote: v.vote,
        })),
      });
    } catch {
      /* local state already updated */
    }
  };

  const handleDeleteDecision = async (decisionId) => {
    if (!selectedMeeting) return;
    updateMeeting({
      ...selectedMeeting,
      decisions: (selectedMeeting.decisions || []).filter(
        (d) => d.id !== decisionId,
      ),
    });
    try {
      await deleteMeetingDecision(selectedMeeting.id, decisionId);
    } catch {
      /* local state already updated */
    }
  };

  // ── Create Meeting ──────────────────────────────────────────────────────────

  const addAgendaItem = () => {
    if (!newMeeting.newAgendaTitle.trim()) return;
    setNewMeeting((prev) => ({
      ...prev,
      agendaItems: [
        ...prev.agendaItems,
        {
          id: Date.now().toString(),
          title: prev.newAgendaTitle,
          description: prev.newAgendaDescription,
        },
      ],
      newAgendaTitle: "",
      newAgendaDescription: "",
    }));
  };

  const removeAgendaItem = (id) =>
    setNewMeeting((prev) => ({
      ...prev,
      agendaItems: prev.agendaItems.filter((a) => a.id !== id),
    }));

  const inviteeRowKey = (i) => i.inviteKey || i.email;

  const toggleInvitee = (person, category) => {
    const key = person.inviteKey || person.email;
    setNewMeeting((prev) => {
      const exists = prev.selectedInvitees.find(
        (i) => inviteeRowKey(i) === key,
      );
      return {
        ...prev,
        selectedInvitees: exists
          ? prev.selectedInvitees.filter((i) => inviteeRowKey(i) !== key)
          : [
              ...prev.selectedInvitees,
              {
                ...person,
                category,
                inviteKey: key,
                external: !!person.external,
              },
            ],
      };
    });
  };

  const addExternalInvitee = () => {
    const name = (newMeeting.manualName || "").trim();
    if (!name) {
      toast.error(
        t("externalGuestNameRequired", {
          defaultValue: "Enter a name for the external guest.",
        }),
      );
      return;
    }
    const key = `ext-${Date.now()}`;
    const email = (newMeeting.manualEmail || "").trim();
    setNewMeeting((prev) => ({
      ...prev,
      selectedInvitees: [
        ...prev.selectedInvitees,
        {
          name,
          email,
          category: t("cat_external", { defaultValue: "External" }),
          inviteKey: key,
          external: true,
        },
      ],
      manualName: "",
      manualEmail: "",
    }));
  };

  const removeInviteeByKey = (key) =>
    setNewMeeting((p) => ({
      ...p,
      selectedInvitees: p.selectedInvitees.filter(
        (i) => inviteeRowKey(i) !== key,
      ),
    }));

  const addExternalLink = () => {
    if (!newMeeting.newLinkTitle.trim() || !newMeeting.newLinkUrl.trim()) {
      toast.error(
        t("linkTitleUrlRequired", {
          defaultValue: "Enter both title and URL for the link.",
        }),
      );
      return;
    }
    setNewMeeting((prev) => ({
      ...prev,
      externalLinks: [
        ...prev.externalLinks,
        {
          id: Date.now().toString(),
          title: prev.newLinkTitle.trim(),
          url: prev.newLinkUrl.trim(),
        },
      ],
      newLinkTitle: "",
      newLinkUrl: "",
    }));
  };

  const removeExternalLink = (id) =>
    setNewMeeting((p) => ({
      ...p,
      externalLinks: p.externalLinks.filter((l) => l.id !== id),
    }));

  const onCreateDialogPrepFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    setNewMeeting((prev) => ({
      ...prev,
      draftPrepDocuments: [
        ...prev.draftPrepDocuments,
        ...files.map((file) => ({
          id: `${Date.now()}-${file.name}-${Math.random()}`,
          file,
        })),
      ],
    }));
    if (createPrepDocsInputRef.current)
      createPrepDocsInputRef.current.value = "";
  };

  const removeDraftPrepDoc = (id) =>
    setNewMeeting((p) => ({
      ...p,
      draftPrepDocuments: p.draftPrepDocuments.filter((d) => d.id !== id),
    }));

  const handleCreateMeeting = async () => {
    if (!newMeeting.name.trim() || !newMeeting.startDate) {
      toast.error(t("fillNameDate"));
      return;
    }
    const snapshot = { ...newMeeting };
    const d = new Date(snapshot.startDate);

    const buildParticipants = () => {
      let chairAssigned = false;
      return snapshot.selectedInvitees.map((inv, idx) => {
        if (inv.external) {
          return {
            id: `p-temp-${idx}`,
            name: inv.name,
            role: t("role_externalGuest"),
            present: false,
          };
        }
        if (!chairAssigned) {
          chairAssigned = true;
          return {
            id: `p-temp-${idx}`,
            name: inv.name,
            role: t("role_chair"),
            present: false,
          };
        }
        return {
          id: `p-temp-${idx}`,
          name: inv.name,
          role: t("role_participant"),
          present: false,
        };
      });
    };

    const localMeeting = {
      id: `temp-${Date.now()}`,
      date: snapshot.startDate,
      name: snapshot.name,
      type: t("type_boardMeeting"),
      quarter: Math.ceil((d.getMonth() + 1) / 3),
      location: snapshot.location || undefined,
      onlineLink: snapshot.onlineLink || undefined,
      meetingFormat: snapshot.meetingFormat,
      attendees: snapshot.selectedInvitees.map((i) => i.name),
      documents: (snapshot.draftPrepDocuments || []).map((row) => ({
        id: `draft-${row.id}`,
        name: row.file.name,
        type: "other",
        size: row.file.size ? `${(row.file.size / 1024).toFixed(1)} KB` : "—",
        url: "",
        content: "",
        notes: [],
      })),
      status: "upcoming",
      agendaItems: snapshot.agendaItems.map((a) => ({ ...a, minutes: "" })),
      photos: [],
      participants: buildParticipants(),
      todos: [],
      followUpDocs: [],
      questions: [],
      expectedDecisions: snapshot.expectedDecisions || [],
      decisions: (snapshot.expectedDecisions || []).map((exp) => ({
        id: `dec-${Date.now()}-${exp.id}`,
        title: exp.title,
        decisionType: exp.decisionType,
        expectedOutcome: exp.expectedOutcome,
        decisionText: "",
        votes: [],
        decisionOwner: "",
        followUpRequired: false,
        stakeholdersAffected: [],
        status: "pending",
      })),
      externalGuests: snapshot.selectedInvitees
        .filter((i) => i.external)
        .map((i, idx) => ({
          id: `local-ext-${idx}`,
          name: i.name,
          email: i.email || "",
        })),
      materialLinks: (snapshot.externalLinks || []).map((l) => ({
        id: `local-ml-${l.id}`,
        title: l.title,
        url: l.url,
      })),
    };
    setMeetings((prev) => [...prev, localMeeting]);
    const createdYear = new Date(snapshot.startDate).getFullYear();
    if (!Number.isNaN(createdYear)) setSelectedYear(createdYear);
    setSelectedMeeting(localMeeting);
    setActiveFolder("preparation");
    setViewingPrepDoc(null);
    setViewingDoc(null);
    setIsCreateOpen(false);
    setNewMeeting({
      name: "",
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      location: "",
      onlineLink: "",
      meetingFormat: "in-person",
      selectedInvitees: [],
      agendaItems: [],
      newAgendaTitle: "",
      newAgendaDescription: "",
      externalLinks: [],
      newLinkTitle: "",
      newLinkUrl: "",
      manualName: "",
      manualEmail: "",
      expectedDecisions: [],
      draftPrepDocuments: [],
    });
    try {
      const emailAttendees = snapshot.selectedInvitees
        .filter((i) => !i.external && (i.email || "").trim())
        .map((i) => i.email.trim());
      const externalGuestsPayload = snapshot.selectedInvitees
        .filter((i) => i.external)
        .map((i) => {
          const name = (i.name || "").trim();
          const email = (i.email || "").trim();
          if (!name) return null;
          return email ? { name, email } : { name };
        })
        .filter(Boolean);
      const materialLinksPayload = (snapshot.externalLinks || [])
        .map((l) => ({
          title: (l.title || "").trim(),
          url: (l.url || "").trim(),
        }))
        .filter((l) => l.title && l.url);
      const payload = {
        name: (snapshot.name || "").trim(),
        date: snapshot.startDate,
        start_time: snapshot.startTime || undefined,
        end_date: snapshot.endDate || undefined,
        end_time: snapshot.endTime || undefined,
        meeting_format: snapshot.meetingFormat,
        location: snapshot.location?.trim() || undefined,
        online_link: snapshot.onlineLink?.trim() || undefined,
        attendees: emailAttendees,
        external_guests: externalGuestsPayload,
        agenda_items: snapshot.agendaItems.map((a) => ({
          title: (a.title || "").trim(),
          description: (a.description || "").trim() || undefined,
        })),
        expected_decisions: (snapshot.expectedDecisions || []).map((e) => ({
          title: (e.title || "").trim(),
          decision_type: e.decisionType,
          expected_outcome: e.expectedOutcome,
          description: (e.description || "").trim() || undefined,
        })),
        material_links: materialLinksPayload,
      };
      const created = await createBoardMeeting(payload);
      toast.success(t("meetingCreated", { name: localMeeting.name }));
      const newId =
        created?.id ??
        created?.data?.id ??
        (typeof created === "object" && created !== null
          ? created["id"]
          : undefined);
      if (newId != null && String(newId) !== "") {
        const uploads = snapshot.draftPrepDocuments || [];
        for (const row of uploads) {
          try {
            const fd = new FormData();
            fd.append("file", row.file);
            fd.append("name", row.file.name);
            await uploadMeetingDocument(String(newId), fd);
          } catch {
            toast.error(t("uploadOneFailed", { name: row.file.name }));
          }
        }
        try {
          const detail = await fetchBoardMeeting(String(newId));
          if (detail) {
            const normalized = normalizeMeeting(detail);
            setMeetings((prev) =>
              prev.map((m) => (m.id === localMeeting.id ? normalized : m)),
            );
            setSelectedMeeting((prev) =>
              prev?.id === localMeeting.id ? normalized : prev,
            );
          }
        } catch {
          const fallback = normalizeMeeting(
            created?.data && typeof created.data === "object"
              ? created.data
              : created,
          );
          setMeetings((prev) =>
            prev.map((m) => (m.id === localMeeting.id ? fallback : m)),
          );
          setSelectedMeeting((prev) =>
            prev?.id === localMeeting.id ? fallback : prev,
          );
        }
      } else {
        toast.info(t("meetingCreatedSyncIssue"));
      }
    } catch (err) {
      const data = err?.response?.data;
      const rollbackOptimisticCreate = () => {
        setMeetings((prev) => prev.filter((m) => m.id !== localMeeting.id));
        setSelectedMeeting((prev) =>
          prev?.id === localMeeting.id ? null : prev,
        );
        setNewMeeting(snapshot);
        setIsCreateOpen(true);
      };
      if (data?.error === "validation_error" && data?.details) {
        toast.error(t("meetingValidationError"), {
          description: formatMeetingValidationDetails(data.details),
        });
        rollbackOptimisticCreate();
      } else {
        toast.error(t("meetingCreateFailed"));
        rollbackOptimisticCreate();
      }
    }
  };

  // ── Download meeting book ───────────────────────────────────────────────────

  const downloadBook = (m) => {
    const doc = new jsPDF();
    let y = 20;
    const lh = 7;
    doc.setFontSize(20);
    doc.text(m.name, 20, y);
    y += lh * 2;
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(t("pdfDetails"), 20, y);
    y += lh;
    doc.setFont("helvetica", "normal");
    doc.text(t("pdfDate", { date: formatMeetingDate(m.date) }), 20, y);
    y += lh;
    if (m.location) {
      doc.text(t("pdfLocation", { location: m.location }), 20, y);
      y += lh;
    }
    y += lh;
    doc.setFont("helvetica", "bold");
    doc.text(t("pdfAttendees"), 20, y);
    y += lh;
    doc.setFont("helvetica", "normal");
    (m.attendees || []).forEach((a) => {
      doc.text(`• ${a}`, 25, y);
      y += lh;
    });
    if (m.externalGuests?.length) {
      y += lh;
      doc.setFont("helvetica", "bold");
      doc.text(t("pdfExternalGuests"), 20, y);
      y += lh;
      doc.setFont("helvetica", "normal");
      m.externalGuests.forEach((g) => {
        const line = g.email ? `• ${g.name} (${g.email})` : `• ${g.name}`;
        doc.text(line, 25, y);
        y += lh;
      });
    }
    if (m.materialLinks?.length) {
      y += lh;
      doc.setFont("helvetica", "bold");
      doc.text(t("pdfMaterialLinks"), 20, y);
      y += lh;
      doc.setFont("helvetica", "normal");
      m.materialLinks.forEach((l) => {
        doc.text(`• ${l.title}: ${l.url}`, 25, y);
        y += lh;
      });
    }
    doc.save(`${m.name.replace(/\s+/g, "_")}_Meeting_Book.pdf`);
    toast.success(t("bookDownloaded"));
  };

  // ── Folder content renderer ─────────────────────────────────────────────────

  const renderFolderContent = () => {
    if (!selectedMeeting) return null;
    const m = selectedMeeting;
    const isLocked = !!lockedMinutes[m.id];
    const sigs = minutesSigs[m.id] || [];
    const userPendingSig = sigs.some(
      (s) => s.name === currentUser.name && s.status === "pending",
    );

    // ── Document viewer (shared) ──────────────────────────────────────────────
    const DocViewer = ({ doc, isPrep, noteState, setNoteState }) => (
      <div
        className="border border-border rounded-lg overflow-hidden flex flex-col"
        style={{ minHeight: 500, height: "calc(100vh - 340px)" }}
      >
        {/* Header */}
        <div className="p-3 bg-muted/50 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() =>
                isPrep ? setViewingPrepDoc(null) : setViewingDoc(null)
              }
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="font-medium text-foreground text-sm">{doc.name}</p>
              <p className="text-xs text-muted-foreground">{doc.size}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => doc.url && window.open(doc.url, "_blank")}
              disabled={!doc.url}
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                isPrep ? setViewingPrepDoc(null) : setViewingDoc(null)
              }
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Content */}
          <div className="flex-1 min-h-0 flex flex-col">
            <ScrollArea className="flex-1">
              <div className="p-4">
                <pre className="whitespace-pre-wrap font-sans text-sm text-foreground/90 leading-relaxed">
                  {doc.content || t("noContentPreview")}
                </pre>
              </div>
            </ScrollArea>
            {/* Penneo panel */}
            {docSigStatus[doc.id] !== "signed" && (
              <div className="p-4 border-t border-border bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center animate-pulse">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-300">
                        {t("pendingYourSignature")}
                      </p>
                      <p className="text-xs text-amber-700/80">
                        {t("penneoDocHint")}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => signDoc(doc.id, doc.name)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
                  >
                    <PenTool className="w-4 h-4" /> {t("signWithPenneo")}
                  </Button>
                </div>
              </div>
            )}
            {docSigStatus[doc.id] === "signed" && (
              <div className="p-4 border-t border-border bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">
                      {t("documentSigned")}
                    </p>
                    <p className="text-xs text-green-600/80">
                      {t("viaPenneoOn", {
                        date: new Date().toLocaleDateString(locale),
                      })}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Notes panel */}
          <div className="w-1/3 border-l border-border flex flex-col bg-muted/20 min-h-0">
            <div className="p-3 border-b border-border shrink-0">
              <h5 className="text-sm font-medium text-foreground flex items-center gap-2">
                <MessageSquare className="w-4 h-4" /> {t("notes")}
              </h5>
            </div>
            <ScrollArea className="flex-1 p-3">
              <div className="space-y-3">
                {doc.notes.map((note) => (
                  <div
                    key={note.id}
                    className="p-2 bg-background rounded-md border border-border/50"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <User className="w-3 h-3 text-muted-foreground" />
                        <span className="text-xs font-medium text-foreground">
                          {note.author}
                        </span>
                      </div>
                      {note.author === currentUser.name && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => deleteNote(note.id, doc.id, isPrep)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-foreground/80">{note.content}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {note.createdAt}
                    </p>
                  </div>
                ))}
                {doc.notes.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {t("noNotesYet")}
                  </p>
                )}
              </div>
            </ScrollArea>
            <div className="p-3 border-t border-border shrink-0">
              <div className="flex gap-2">
                <Input
                  placeholder={t("addNotePh")}
                  className="text-xs"
                  value={isPrep ? newPrepNote : newNoteContent}
                  onChange={(e) =>
                    isPrep
                      ? setNewPrepNote(e.target.value)
                      : setNewNoteContent(e.target.value)
                  }
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    addNote(
                      doc.id,
                      isPrep ? newPrepNote : newNoteContent,
                      isPrep,
                    )
                  }
                />
                <Button
                  size="sm"
                  onClick={() =>
                    addNote(
                      doc.id,
                      isPrep ? newPrepNote : newNoteContent,
                      isPrep,
                    )
                  }
                >
                  <Send className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );

    switch (activeFolder) {
      // ── Preparation ─────────────────────────────────────────────────────────
      case "preparation":
        if (viewingPrepDoc) {
          return (
            <DocViewer
              doc={viewingPrepDoc}
              isPrep={true}
              noteState={newPrepNote}
              setNoteState={setNewPrepNote}
            />
          );
        }
        return (
          <div className="space-y-6">
            {m.status === "upcoming" && (
              <PreMeetingPrompt
                meetingName={m.name}
                meetingDate={m.date}
                onNavigateToStrategy={() =>
                  toast.info(t("navigateStrategyFundamentals"))
                }
              />
            )}
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {t("meetingDocuments")}
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => downloadBook(m)}
              >
                <Download className="w-4 h-4 mr-2" /> {t("downloadMeetingBook")}
              </Button>
            </div>
            {m.materialLinks?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  {t("materialLinksHeading", {
                    defaultValue: "Material links",
                  })}
                </h4>
                {m.materialLinks.map((link) => (
                  <div
                    key={link.id}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        window.open(link.url, "_blank", "noopener,noreferrer");
                    }}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() =>
                      window.open(link.url, "_blank", "noopener,noreferrer")
                    }
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <ExternalLink className="w-5 h-5 text-primary shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-foreground truncate">
                          {link.title}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {link.url}
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              {m.documents.length > 0 && (
                <h4 className="text-sm font-medium text-foreground flex items-center gap-2 pt-1">
                  <FileText className="w-4 h-4" />
                  {t("uploadedDocumentsHeading", {
                    defaultValue: "Uploaded documents",
                  })}
                </h4>
              )}
              {m.documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => setViewingPrepDoc(doc)}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-primary" />
                    <div>
                      <p className="font-medium text-foreground">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {doc.size}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {doc.notes.length > 0 && (
                      <Badge variant="secondary" className="text-xs">
                        {t("notesCount", { count: doc.notes.length })}
                      </Badge>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (doc.url) window.open(doc.url, "_blank");
                      }}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </div>
              ))}
              {m.documents.length === 0 &&
                (m.materialLinks?.length || 0) === 0 && (
                  <p className="text-muted-foreground text-center py-8">
                    {t("noDocumentsYet")}
                  </p>
                )}
            </div>
            <input
              type="file"
              ref={prepDocInputRef}
              className="hidden"
              onChange={(e) => {
                handleUploadPrepDoc(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => prepDocInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" /> {t("uploadDocument")}
            </Button>
          </div>
        );

      // ── Board Signal ─────────────────────────────────────────────────────────
      case "board-signal":
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {t("boardSignal")}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t("boardSignalSubtitle")}
              </p>
            </div>
            <BoardSignalSummary />
          </div>
        );

      // ── Questions ────────────────────────────────────────────────────────────
      case "questions":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              {t("questions")}
            </h3>
            <div className="space-y-3">
              {m.questions.map((q) => (
                <div
                  key={q.id}
                  className="p-4 bg-muted/30 rounded-lg border border-border/50"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-foreground">
                        {q.question}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("askedBy", { author: q.author })}
                      </p>
                    </div>
                    <Badge variant={q.answered ? "default" : "secondary"}>
                      {q.answered ? t("answered") : t("pending")}
                    </Badge>
                  </div>
                  {q.answer && (
                    <div className="mt-3 pt-3 border-t border-border/50">
                      <p className="text-sm text-muted-foreground">
                        {q.answer}
                      </p>
                    </div>
                  )}
                </div>
              ))}
              {m.questions.length === 0 && (
                <p className="text-muted-foreground text-center py-8">
                  No questions yet
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={t("questionPh")}
                value={newQuestion}
                onChange={(e) => setNewQuestion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addQuestion()}
              />
              <Button onClick={addQuestion}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        );

      // ── Minutes ──────────────────────────────────────────────────────────────
      case "minutes":
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {t("meetingMinutes")}
              </h3>
              {isLocked && (
                <Badge
                  variant="secondary"
                  className="gap-1 bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300"
                >
                  <Lock className="w-3 h-3" /> {t("lockedSentApproval")}
                </Badge>
              )}
            </div>

            {isLocked && (
              <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Lock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-800 dark:text-amber-300">
                      {t("minutesLockedTitle")}
                    </p>
                    <p className="text-sm text-amber-700/80 mt-1">
                      {t("minutesLockedBody")}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => restartSigning(m.id)}
                    className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300"
                  >
                    <RotateCcw className="w-3 h-3" /> {t("restart")}
                  </Button>
                </div>
              </div>
            )}

            {m.agendaItems.length > 0 ? (
              <div className="space-y-4">
                {m.agendaItems.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`bg-muted/30 rounded-lg border border-border/50 overflow-hidden ${isLocked ? "opacity-75" : ""}`}
                  >
                    <div className="p-3 bg-muted/50 border-b border-border/50">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {idx + 1}
                        </Badge>
                        <h4 className="font-medium text-foreground">
                          {item.title}
                        </h4>
                      </div>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-1 ml-7">
                          {item.description}
                        </p>
                      )}
                    </div>
                    <div className="p-3">
                      <Label className="text-xs text-muted-foreground mb-2 block">
                        {t("minutesForAgendaItem")}
                      </Label>
                      {isLocked ? (
                        <div className="p-3 bg-muted/50 rounded-md border border-border/50 min-h-[72px]">
                          <p className="text-sm text-foreground whitespace-pre-wrap">
                            {item.minutes || (
                              <span className="text-muted-foreground italic">
                                {t("noNotes")}
                              </span>
                            )}
                          </p>
                        </div>
                      ) : (
                        <Textarea
                          placeholder={t("recordMinutesPh")}
                          rows={3}
                          className="text-sm resize-none"
                          value={editingMinutes[item.id] ?? item.minutes ?? ""}
                          onChange={(e) =>
                            setEditingMinutes((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }))
                          }
                          onBlur={() => {
                            if (editingMinutes[item.id] !== undefined) {
                              updateAgendaMinutes(
                                item.id,
                                editingMinutes[item.id],
                              );
                            }
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClipboardList className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">{t("noAgendaItems")}</p>
              </div>
            )}

            {/* Board Decisions Section */}
            <div className="pt-6 border-t border-border">
              <DecisionBlocksSection
                decisions={m.decisions || []}
                participants={m.participants.map((p) => ({
                  id: p.id,
                  name: p.name,
                  role: p.role,
                }))}
                isLocked={isLocked}
                onDecisionsChange={async (newDecisions) => {
                  const oldDecisions = m.decisions || [];
                  // Optimistic local update
                  updateMeeting({ ...m, decisions: newDecisions });

                  // Persist adds
                  for (const d of newDecisions) {
                    if (!oldDecisions.some((od) => od.id === d.id)) {
                      try {
                        const res = await createMeetingDecision(m.id, {
                          title: d.title,
                          decision_type: d.decisionType,
                          expected_outcome: d.expectedOutcome,
                          status: d.status || "pending",
                        });
                        if (res?.id) {
                          setMeetings((prev) =>
                            prev.map((x) =>
                              x.id === m.id
                                ? {
                                    ...x,
                                    decisions: x.decisions.map((dec) =>
                                      dec.id === d.id
                                        ? { ...dec, id: String(res.id) }
                                        : dec,
                                    ),
                                  }
                                : x,
                            ),
                          );
                        }
                      } catch {
                        /* optimistic state kept */
                      }
                    }
                  }

                  // Persist removes
                  for (const od of oldDecisions) {
                    if (!newDecisions.some((d) => d.id === od.id)) {
                      try {
                        await deleteMeetingDecision(m.id, od.id);
                      } catch {}
                    }
                  }

                  // Persist updates
                  for (const d of newDecisions) {
                    const old = oldDecisions.find((od) => od.id === d.id);
                    if (old && JSON.stringify(old) !== JSON.stringify(d)) {
                      try {
                        await updateMeetingDecision(m.id, d.id, {
                          decision_text: d.decisionText,
                          decision_owner: d.decisionOwner,
                          status: d.status,
                          follow_up_required: d.followUpRequired,
                          follow_up_description: d.followUpDescription,
                          review_date: d.reviewDate || null,
                          votes: (d.votes || []).map((v) => ({
                            participant_name:
                              v.participantName || v.participant_name,
                            vote: v.vote,
                          })),
                        });
                      } catch {}
                    }
                  }
                }}
              />
            </div>

            {/* General meeting notes */}
            {m.minutes && (
              <div className="pt-4 border-t border-border">
                <Label className="text-sm font-medium text-foreground mb-2 block">
                  {t("generalMeetingNotes")}
                </Label>
                <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                  <p className="text-foreground whitespace-pre-wrap text-sm">
                    {m.minutes}
                  </p>
                </div>
              </div>
            )}

            {/* Signature status */}
            {sigs.length > 0 && (
              <div className="pt-4 border-t border-border">
                <div className="bg-muted/30 rounded-lg border border-border/50 overflow-hidden">
                  <div className="p-3 bg-muted/50 border-b border-border/50 flex items-center justify-between">
                    <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                      <PenTool className="w-4 h-4" /> {t("signatureStatus")}
                    </h4>
                    <Badge variant="secondary" className="text-xs">
                      {t("signedFraction", {
                        signed: sigs.filter((s) => s.status === "signed")
                          .length,
                        total: sigs.length,
                      })}
                    </Badge>
                  </div>
                  <div className="p-3 space-y-2">
                    {sigs.map((sig, idx) => (
                      <div
                        key={idx}
                        className={`flex items-center justify-between p-2 rounded-md ${
                          sig.status === "signed"
                            ? "bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800"
                            : sig.status === "declined"
                              ? "bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800"
                              : "bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {sig.status === "signed" ? (
                            <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : sig.status === "declined" ? (
                            <X className="w-4 h-4 text-red-600 dark:text-red-400" />
                          ) : (
                            <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                          )}
                          <span className="text-sm font-medium">
                            {sig.name}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {sig.status === "signed" && sig.signedAt ? (
                            <span className="text-green-600 dark:text-green-400">
                              {t("signedLabel", { at: sig.signedAt })}
                            </span>
                          ) : sig.status === "pending" ? (
                            <span className="text-amber-600 dark:text-amber-400">
                              {t("pending")}
                            </span>
                          ) : sig.status === "declined" ? (
                            <span className="text-red-600 dark:text-red-400">
                              {t("declined")}
                            </span>
                          ) : null}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Sign button for current user */}
            {userPendingSig && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center animate-pulse">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-300">
                        {t("awaitingYourSignature")}
                      </p>
                      <p className="text-xs text-amber-700/80">
                        {t("minutesPenneoHint")}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={() => signMinutes(m.id)}
                    className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white gap-2"
                  >
                    <PenTool className="w-4 h-4" /> {t("signWithPenneo")}
                  </Button>
                </div>
              </div>
            )}

            {!isLocked && (
              <>
                <Separator />
                <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
                  <div>
                    <h4 className="text-sm font-medium text-foreground">
                      {t("readyForApproval")}
                    </h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t("sendApprovalHint", {
                        count:
                          m.participants.filter((p) => p.present).length ||
                          m.attendees.length,
                      })}
                    </p>
                  </div>
                  <Button
                    onClick={() => sendToApproval(m.id)}
                    className="gap-2"
                  >
                    <Send className="w-4 h-4" /> {t("sendForApproval")}
                  </Button>
                </div>
              </>
            )}
          </div>
        );

      // ── Pictures ─────────────────────────────────────────────────────────────
      case "pictures":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              {t("meetingPictures")}
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {m.photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative aspect-video bg-muted rounded-lg overflow-hidden border border-border/50"
                >
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/60 to-transparent">
                    <p className="text-white text-xs">{photo.name}</p>
                    <Badge variant="secondary" className="text-xs mt-1">
                      {photo.category}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
            {m.photos.length === 0 && (
              <div className="text-center py-8">
                <Camera className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">{t("noPicturesYet")}</p>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              ref={photoInputRef}
              className="hidden"
              onChange={(e) => {
                handleUploadPhoto(e.target.files?.[0]);
                e.target.value = "";
              }}
            />
            <Button
              variant="outline"
              className="w-full"
              onClick={() => photoInputRef.current?.click()}
            >
              <Upload className="w-4 h-4 mr-2" /> {t("uploadPictures")}
            </Button>
          </div>
        );

      // ── Participants ──────────────────────────────────────────────────────────
      case "participants":
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">
                {t("participants")}
              </h3>
              <Badge variant="secondary">
                {t("presentFraction", {
                  present: m.participants.filter((p) => p.present).length,
                  total: m.participants.length,
                })}
              </Badge>
            </div>
            <div className="space-y-2">
              {m.participants.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50"
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={p.present}
                      onCheckedChange={() => togglePresence(p.id)}
                      aria-label={p.name}
                    />
                    <button
                      type="button"
                      className="flex-1 text-left bg-transparent border-0 p-0 cursor-pointer min-w-0"
                      onClick={() => togglePresence(p.id)}
                    >
                      <p className="font-medium text-foreground">{p.name}</p>
                      <p className="text-xs text-muted-foreground">{p.role}</p>
                    </button>
                  </div>
                  <Badge variant={p.present ? "default" : "outline"}>
                    {p.present ? t("present") : t("absent")}
                  </Badge>
                </div>
              ))}
              {m.externalGuests?.length > 0 && (
                <div className="pt-4 space-y-2 border-t border-border">
                  <h4 className="text-sm font-medium text-foreground">
                    {t("externalGuestsHeading", {
                      defaultValue: "External guests (expected)",
                    })}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {t("externalGuestsListHint", {
                      defaultValue:
                        "Shown for visibility; not part of the invitee email list.",
                    })}
                  </p>
                  {m.externalGuests.map((g) => (
                    <div
                      key={g.id}
                      className="flex items-center justify-between p-3 bg-muted/20 rounded-lg border border-dashed border-border"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <User className="w-4 h-4 text-muted-foreground shrink-0" />
                        <div className="min-w-0">
                          <p className="font-medium text-foreground">
                            {g.name}
                          </p>
                          {g.email ? (
                            <p className="text-xs text-muted-foreground truncate">
                              {g.email}
                            </p>
                          ) : null}
                        </div>
                      </div>
                      <Badge variant="outline" className="shrink-0">
                        {t("role_externalGuest")}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
              {m.participants.length === 0 &&
                (m.externalGuests?.length || 0) === 0 && (
                  <p className="text-muted-foreground text-center py-8">
                    {t("noParticipantsYet")}
                  </p>
                )}
            </div>
          </div>
        );

      // ── Follow Up ─────────────────────────────────────────────────────────────
      case "document-yield":
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-foreground">
              {t("followUp")}
            </h3>

            {/* Board direction recording for completed meetings */}
            {m.status === "completed" && (
              <>
                <PostMeetingOutcome
                  meetingId={m.id}
                  meetingName={m.name}
                  currentUserName={currentUser.name}
                  onRecordOutcome={(itemId, outcome) => {
                    toast.success(t("boardDirectionRecorded"));
                  }}
                />
                <Separator />
              </>
            )}

            {/* Feedback */}
            <div className="bg-muted/30 rounded-lg border border-border/50 p-4">
              <h4 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                <Star className="w-4 h-4" /> {t("postMeetingFeedback")}
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  "feedbackEffectiveness",
                  "feedbackPreparation",
                  "feedbackParticipation",
                  "feedbackDecisions",
                  "feedbackTimeManagement",
                  "feedbackStrengths",
                  "feedbackImprovements",
                ].map((key) => (
                  <div key={key}>
                    <Label className="text-xs">{t(key)}</Label>
                    <Textarea
                      placeholder={t("rateFieldPh", {
                        field: t(key).toLowerCase(),
                      })}
                      rows={2}
                      className="mt-1 text-sm resize-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Todos */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <CheckSquare className="w-4 h-4" /> {t("actionItemsTodos")}
              </h4>
              <div className="space-y-2">
                {m.todos.map((todo) => (
                  <div
                    key={todo.id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50"
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={todo.completed}
                        onCheckedChange={() => toggleTodo(todo.id)}
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      />
                      <div>
                        <p
                          className={`font-medium ${todo.completed ? "line-through text-muted-foreground" : "text-foreground"}`}
                        >
                          {todo.task}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t("assignedToDue", {
                            assignee: todo.assignee,
                            date: formatMeetingDate(todo.dueDate),
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 space-y-2">
                <Input
                  placeholder={t("newTodoPh")}
                  value={newTodo.task}
                  onChange={(e) =>
                    setNewTodo((p) => ({ ...p, task: e.target.value }))
                  }
                />
                <div className="flex gap-2">
                  <Input
                    placeholder={t("assignee")}
                    value={newTodo.assignee}
                    onChange={(e) =>
                      setNewTodo((p) => ({ ...p, assignee: e.target.value }))
                    }
                  />
                  <Input
                    type="date"
                    value={newTodo.dueDate}
                    onChange={(e) =>
                      setNewTodo((p) => ({ ...p, dueDate: e.target.value }))
                    }
                  />
                  <Button onClick={addTodo} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            <Separator />

            {/* Follow-up docs */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4" /> {t("followUpDocuments")}
              </h4>
              {viewingDoc ? (
                <DocViewer
                  doc={viewingDoc}
                  isPrep={false}
                  noteState={newNoteContent}
                  setNoteState={setNewNoteContent}
                />
              ) : (
                <>
                  <div className="space-y-2">
                    {m.followUpDocs.map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => setViewingDoc(doc)}
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-primary" />
                          <div>
                            <p className="font-medium text-foreground">
                              {doc.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {doc.size}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {doc.notes.length > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {t("notesCount", { count: doc.notes.length })}
                            </Badge>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (doc.url) window.open(doc.url, "_blank");
                            }}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                    {m.followUpDocs.length === 0 && (
                      <p className="text-muted-foreground text-sm">
                        {t("noFollowUpDocs")}
                      </p>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={followupDocInputRef}
                    className="hidden"
                    onChange={(e) => {
                      handleUploadFollowupDoc(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    variant="outline"
                    className="w-full mt-3"
                    onClick={() => followupDocInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" /> {t("uploadDocument")}
                  </Button>
                </>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ── Data for left panel (group by YYYY-MM so structure never depends on locale; labels use safe Intl) ──
  const { yearMeetings, monthSections } = useMemo(() => {
    const ym = getMeetingsForYear(meetings, selectedYear);
    const byKey = {};
    ym.forEach((m) => {
      const d = new Date(m.date);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!byKey[key]) byKey[key] = [];
      byKey[key].push(m);
    });
    const monthSections = Object.keys(byKey)
      .sort()
      .map((monthKey) => {
        const [ys, ms] = monthKey.split("-").map(Number);
        const label = safeToLocaleDateString(
          new Date(Date.UTC(ys, ms - 1, 1)),
          locale,
          { month: "long", year: "numeric" },
        );
        return { monthKey, label, meetings: byKey[monthKey] };
      });
    return { yearMeetings: ym, monthSections };
  }, [meetings, selectedYear, locale]);

  return (
    <div
      ref={meetingLayoutRef}
      className="flex flex-col w-full px-4 pb-4"
      style={{ minHeight: "500px" }}
    >
      <MeetingTabHero t={t} />

      {/* Create Meeting */}
      <div className="mb-4 shrink-0">
        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="w-4 h-4" /> {t("createMeeting")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col overflow-hidden p-6 gap-4">
            <DialogHeader className="shrink-0">
              <DialogTitle className="flex items-center justify-center gap-2 pr-8 sm:justify-start">
                <Calendar className="w-5 h-5" /> {t("createNewMeeting")}
              </DialogTitle>
            </DialogHeader>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-6">
              <div>
                <Label htmlFor="m-name">{t("meetingName")}</Label>
                <Input
                  id="m-name"
                  placeholder={t("meetingNamePh")}
                  value={newMeeting.name}
                  onChange={(e) =>
                    setNewMeeting((p) => ({ ...p, name: e.target.value }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>{t("startDate")}</Label>
                  <Input
                    type="date"
                    value={newMeeting.startDate}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        startDate: e.target.value,
                        endDate: p.endDate || e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>{t("startTime")}</Label>
                  <Input
                    type="time"
                    value={newMeeting.startTime}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        startTime: e.target.value,
                      }))
                    }
                  />
                </div>
                <div>
                  <Label>{t("endDate")}</Label>
                  <Input
                    type="date"
                    min={newMeeting.startDate}
                    value={newMeeting.endDate}
                    onChange={(e) =>
                      setNewMeeting((p) => ({ ...p, endDate: e.target.value }))
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    {t("endDateHint")}
                  </p>
                </div>
                <div>
                  <Label>{t("endTime")}</Label>
                  <Input
                    type="time"
                    value={newMeeting.endTime}
                    onChange={(e) =>
                      setNewMeeting((p) => ({ ...p, endTime: e.target.value }))
                    }
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>{t("meetingFormat")}</Label>
                  <Select
                    value={newMeeting.meetingFormat}
                    onValueChange={(v) =>
                      setNewMeeting((p) => ({ ...p, meetingFormat: v }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" /> {t("format_in_person")}
                        </div>
                      </SelectItem>
                      <SelectItem value="online">
                        <div className="flex items-center gap-2">
                          <Video className="w-4 h-4" /> {t("format_online")}
                        </div>
                      </SelectItem>
                      <SelectItem value="hybrid">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" /> {t("format_hybrid")}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {(newMeeting.meetingFormat === "in-person" ||
                  newMeeting.meetingFormat === "hybrid") && (
                  <div>
                    <Label>{t("location")}</Label>
                    <Input
                      placeholder={t("locationPh")}
                      value={newMeeting.location}
                      onChange={(e) =>
                        setNewMeeting((p) => ({
                          ...p,
                          location: e.target.value,
                        }))
                      }
                    />
                  </div>
                )}
                <div>
                  <Label>{t("onlineMeetingLink")}</Label>
                  <Input
                    placeholder={t("onlineLinkPh")}
                    value={newMeeting.onlineLink}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        onlineLink: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="p-3 bg-muted/30 rounded-lg border">
                <Label className="text-xs text-muted-foreground">
                  {t("convenedBy")}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <User className="w-4 h-4 text-primary" />
                  <span className="font-medium">{currentUser.name || "—"}</span>
                  <span className="text-sm text-muted-foreground">
                    {currentUser.email ? `(${currentUser.email})` : ""}
                  </span>
                </div>
              </div>

              <Separator />

              {/* Invitees */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <UserPlus className="w-4 h-4" /> {t("invitees")}
                </Label>
                {newMeeting.selectedInvitees.length > 0 && (
                  <div className="mb-4 flex flex-wrap gap-2">
                    {newMeeting.selectedInvitees.map((inv) => (
                      <Badge
                        key={inviteeRowKey(inv)}
                        variant="secondary"
                        className="flex items-center gap-1 pr-1 max-w-full"
                      >
                        <span className="truncate">
                          {inv.name}
                          {inv.external && (
                            <span className="text-xs text-muted-foreground ml-1">
                              (
                              {t("externalNoEmailBadge", {
                                defaultValue: "no invite email",
                              })}
                              )
                            </span>
                          )}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-4 w-4 p-0 ml-1 shrink-0"
                          type="button"
                          onClick={() => removeInviteeByKey(inviteeRowKey(inv))}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
                <div className="space-y-3">
                  {Object.entries(inviteeCategories).map(([cat, members]) => (
                    <div key={cat} className="border rounded-lg">
                      <div className="p-2 bg-muted/30 border-b">
                        <span className="text-sm font-medium">{cat}</span>
                      </div>
                      <div className="p-2 space-y-1">
                        {members.map((member) => {
                          const selected = newMeeting.selectedInvitees.some(
                            (i) => inviteeRowKey(i) === member.email,
                          );
                          const person = {
                            name: member.name,
                            email: member.email,
                            inviteKey: member.email,
                            external: false,
                          };
                          return (
                            <div
                              key={member.email}
                              className={`flex items-center gap-2 p-2 rounded-md transition-colors ${selected ? "bg-primary/10 border border-primary" : "hover:bg-muted/50"}`}
                            >
                              <Checkbox
                                checked={selected}
                                onCheckedChange={() =>
                                  toggleInvitee(person, cat)
                                }
                                aria-label={member.name}
                              />
                              <button
                                type="button"
                                className="flex-1 flex flex-wrap items-center gap-x-2 gap-y-0 text-left min-w-0 bg-transparent border-0 p-0 cursor-pointer"
                                onClick={() => toggleInvitee(person, cat)}
                              >
                                <span className="text-sm">{member.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  ({member.role})
                                </span>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 p-3 bg-muted/20 rounded-lg border border-dashed space-y-2">
                  <p className="text-sm font-medium text-foreground">
                    {t("externalGuestsSection", {
                      defaultValue: "External participants",
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {t("externalGuestsHint", {
                      defaultValue:
                        "Listed on the meeting for everyone to see who is expected. No invitation email is sent from here.",
                    })}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <Input
                      placeholder={t("externalGuestNamePh", {
                        defaultValue: "Name *",
                      })}
                      value={newMeeting.manualName}
                      onChange={(e) =>
                        setNewMeeting((p) => ({
                          ...p,
                          manualName: e.target.value,
                        }))
                      }
                    />
                    <Input
                      placeholder={t("externalGuestEmailPh", {
                        defaultValue: "Email (optional)",
                      })}
                      type="email"
                      value={newMeeting.manualEmail}
                      onChange={(e) =>
                        setNewMeeting((p) => ({
                          ...p,
                          manualEmail: e.target.value,
                        }))
                      }
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={addExternalInvitee}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("addExternalGuest", {
                      defaultValue: "Add external participant",
                    })}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Agenda */}
              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <ClipboardList className="w-4 h-4" /> {t("agendaItems")}
                </Label>
                {newMeeting.agendaItems.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {newMeeting.agendaItems.map((item, idx) => (
                      <div
                        key={item.id}
                        className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg border"
                      >
                        <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-1 rounded">
                          {idx + 1}
                        </span>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{item.title}</p>
                          {item.description && (
                            <p className="text-xs text-muted-foreground mt-1">
                              {item.description}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAgendaItem(item.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2 p-3 bg-muted/20 rounded-lg border border-dashed">
                  <Input
                    placeholder={t("agendaTitlePh")}
                    value={newMeeting.newAgendaTitle}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        newAgendaTitle: e.target.value,
                      }))
                    }
                  />
                  <Textarea
                    placeholder={t("agendaDescPh")}
                    className="min-h-[60px]"
                    value={newMeeting.newAgendaDescription}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        newAgendaDescription: e.target.value,
                      }))
                    }
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addAgendaItem}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" /> {t("addAgendaItem")}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Agenda-level prompts from Business Fundamentals */}
              <AgendaPlanningPrompt
                onAddToAgenda={(item) => {
                  setNewMeeting((prev) => ({
                    ...prev,
                    agendaItems: [
                      ...prev.agendaItems,
                      {
                        id: Date.now().toString(),
                        title: `Business Fundamentals: ${item.headline}`,
                        description: item.explanation || "",
                      },
                    ],
                  }));
                  toast.success(t("addedToAgenda"));
                }}
              />

              <Separator />

              {/* Expected Board Decisions */}
              <ExpectedDecisionsSection
                decisions={newMeeting.expectedDecisions}
                onDecisionsChange={(decisions) =>
                  setNewMeeting((prev) => ({
                    ...prev,
                    expectedDecisions: decisions,
                  }))
                }
              />

              <Separator />

              <div>
                <Label className="flex items-center gap-2 mb-3">
                  <Link className="w-4 h-4" />
                  {t("materialsAndLinks", {
                    defaultValue: "Materials & links",
                  })}
                </Label>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  {t("materialsAndLinksHint", {
                    defaultValue:
                      "Add links to budgets, intranet materials, or external resources. Upload files below — they are attached after the meeting is created.",
                  })}
                </p>
                {newMeeting.externalLinks.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {newMeeting.externalLinks.map((link) => (
                      <div
                        key={link.id}
                        className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg border"
                      >
                        <ExternalLink className="w-4 h-4 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {link.title}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {link.url}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          type="button"
                          className="shrink-0"
                          onClick={() => removeExternalLink(link.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2 p-3 bg-muted/20 rounded-lg border border-dashed">
                  <Input
                    placeholder={t("linkTitlePh", {
                      defaultValue: "Link title (e.g. Q2 budget)",
                    })}
                    value={newMeeting.newLinkTitle}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        newLinkTitle: e.target.value,
                      }))
                    }
                  />
                  <Input
                    placeholder={t("linkUrlPh", {
                      defaultValue: "URL (https://...)",
                    })}
                    value={newMeeting.newLinkUrl}
                    onChange={(e) =>
                      setNewMeeting((p) => ({
                        ...p,
                        newLinkUrl: e.target.value,
                      }))
                    }
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={addExternalLink}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {t("addLink", { defaultValue: "Add link" })}
                  </Button>
                </div>

                <div className="mt-4 space-y-2">
                  <Label className="text-sm">
                    {t("uploadPrepDocuments", {
                      defaultValue: "Upload documents",
                    })}
                  </Label>
                  <input
                    ref={createPrepDocsInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      onCreateDialogPrepFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                  {(newMeeting.draftPrepDocuments || []).length > 0 && (
                    <ul className="space-y-1 text-sm border rounded-md p-2 bg-muted/20">
                      {newMeeting.draftPrepDocuments.map((row) => (
                        <li
                          key={row.id}
                          className="flex items-center justify-between gap-2 py-1 border-b border-border/50 last:border-0"
                        >
                          <span className="truncate">{row.file.name}</span>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 shrink-0"
                            onClick={() => removeDraftPrepDoc(row.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    className="w-full"
                    onClick={() => createPrepDocsInputRef.current?.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {t("uploadBudgetOrDoc", {
                      defaultValue: "Upload budget / document",
                    })}
                  </Button>
                </div>
              </div>
            </div>
            <div className="shrink-0 border-t border-border pt-4 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                {t("cancel")}
              </Button>
              <Button onClick={handleCreateMeeting}>
                <Plus className="w-4 h-4 mr-2" /> {t("createMeeting")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Plain flex split: avoids react-resizable-panels measuring 0% width after tab/i18n reflow (list “missing” in some languages). */}
      <div className="flex flex-1 min-h-[500px] h-full w-full rounded-lg border border-border overflow-hidden">
        {/* Left: Year / Meeting list */}
        <aside className="flex w-[min(100%,320px)] min-w-[240px] max-w-[360px] shrink-0 flex-col min-h-0 bg-muted/20 border-r border-border">
          <div className="p-4 border-b border-border shrink-0">
            <h3 className="font-semibold text-foreground flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {t("meetingArchive")}
              {isLoadingMeetings && (
                <span className="ml-auto w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              )}
            </h3>
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-3 space-y-2">
              {years.map((year) => (
                <div key={year}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2 rounded-md transition-all ${
                      selectedYear === year
                        ? "bg-primary text-primary-foreground font-semibold"
                        : "bg-card hover:bg-accent text-foreground border border-border"
                    }`}
                    onClick={() => {
                      setSelectedYear(year);
                      setSelectedMeeting(null);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {selectedYear === year ? (
                          <FolderOpen className="w-4 h-4" />
                        ) : (
                          <Folder className="w-4 h-4" />
                        )}
                        <span className="text-sm font-medium">{year}</span>
                      </div>
                      <Badge
                        variant={
                          selectedYear === year ? "secondary" : "outline"
                        }
                        className="text-xs"
                      >
                        {getMeetingsForYear(meetings, year).length}
                      </Badge>
                    </div>
                  </button>
                  {selectedYear === year && (
                    <div className="ml-4 mt-2 space-y-1">
                      {yearMeetings.length === 0 ? (
                        <p className="text-xs text-muted-foreground px-2 py-2">
                          {t("noMeetingsThisYear")}
                        </p>
                      ) : (
                        monthSections.map(
                          ({ monthKey, label, meetings: mts }) => (
                            <div key={monthKey}>
                              <p className="text-xs text-muted-foreground font-medium px-2 py-1">
                                {label}
                              </p>
                              {mts.map((mt) => (
                                <Button
                                  key={mt.id}
                                  variant={
                                    selectedMeeting?.id === mt.id
                                      ? "secondary"
                                      : "ghost"
                                  }
                                  className="w-full justify-start text-sm h-auto py-2 px-2"
                                  onClick={() => {
                                    setSelectedMeeting(mt);
                                    setActiveFolder("preparation");
                                    setViewingPrepDoc(null);
                                    setViewingDoc(null);
                                  }}
                                >
                                  <div className="flex flex-col items-start">
                                    <span className="font-medium truncate max-w-[180px]">
                                      {mt.name}
                                    </span>
                                    <span className="text-xs text-muted-foreground">
                                      {formatMeetingDate(mt.date)}
                                    </span>
                                  </div>
                                  <ChevronRight className="w-3 h-3 ml-auto" />
                                </Button>
                              ))}
                            </div>
                          ),
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </ScrollArea>
        </aside>

        {/* Right: Meeting detail */}
        <div className="flex flex-1 flex-col min-w-0 min-h-0 bg-background">
          {selectedMeeting ? (
            <div className="h-full flex flex-col">
              {/* Meeting header */}
              <div className="p-4 border-b border-border bg-background">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-semibold text-foreground">
                        {selectedMeeting.name}
                      </h2>
                      <Badge variant="outline" className="text-xs capitalize">
                        {selectedMeeting.meetingFormat === "online" && (
                          <Video className="w-3 h-3 mr-1" />
                        )}
                        {selectedMeeting.meetingFormat === "in-person" && (
                          <MapPin className="w-3 h-3 mr-1" />
                        )}
                        {selectedMeeting.meetingFormat === "hybrid" && (
                          <Video className="w-3 h-3 mr-1" />
                        )}
                        {formatMeetingFormatLabel(
                          selectedMeeting.meetingFormat,
                        )}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />{" "}
                        {formatMeetingDate(selectedMeeting.date)}
                      </span>
                      {selectedMeeting.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />{" "}
                          {selectedMeeting.location}
                        </span>
                      )}
                      {selectedMeeting.onlineLink && (
                        <a
                          href={selectedMeeting.onlineLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-primary hover:underline"
                        >
                          <Video className="w-3 h-3" /> {t("joinOnline")}{" "}
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                  <Badge
                    variant={
                      selectedMeeting.status === "completed"
                        ? "default"
                        : "secondary"
                    }
                  >
                    {formatStatusLabel(selectedMeeting.status)}
                  </Badge>
                </div>
              </div>

              {/* Folder tabs: dot sits inside button (absolute in padding) so overflow-x-auto on the row does not clip it. */}
              <div className="shrink-0 border-b border-border bg-muted/30">
                <div className="flex overflow-x-auto py-1">
                  {FOLDERS.map((folder) => {
                    const hasContent = folderHasContent(
                      selectedMeeting,
                      folder.id,
                    );
                    const unviewed = isFolderUnviewed(
                      selectedMeeting.id,
                      folder.id,
                    );
                    const boardSignalFlag =
                      folder.id === "board-signal" &&
                      shouldShowBoardSignalFlag();
                    const showDot =
                      boardSignalFlag ||
                      (hasContent && unviewed && activeFolder !== folder.id);
                    return (
                      <Button
                        key={folder.id}
                        variant={
                          activeFolder === folder.id ? "secondary" : "ghost"
                        }
                        className="relative overflow-visible rounded-none border-b-2 border-transparent data-[active=true]:border-primary flex-shrink-0 gap-2 pr-6"
                        data-active={activeFolder === folder.id}
                        onClick={() => handleFolderClick(folder.id)}
                      >
                        {folder.icon}
                        <span className="ml-0">{folder.name}</span>
                        {showDot && (
                          <span
                            className={`pointer-events-none absolute top-1.5 right-2 h-2 w-2 rounded-full bg-destructive ring-2 ring-background ${boardSignalFlag ? "animate-pulse" : ""}`}
                            aria-hidden
                          />
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <ScrollArea className="flex-1 p-6">
                {renderFolderContent()}
              </ScrollArea>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <Calendar className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-muted-foreground mb-2">
                  {t("noMeetingSelected")}
                </h3>
                <p className="text-sm text-muted-foreground/70">
                  {t("selectMeetingHint")}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingSection;
